import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { api } from '@/lib/api';

interface OwnerSession {
  role: 'owner';
  token: string;
  restaurant_code: string;
  restaurant_name: string;
}

interface DriverSession {
  role: 'driver';
  token: string;
  driver_id: number;
  display_name: string;
  restaurant_code: string;
}

type Session = OwnerSession | DriverSession | null;

interface AuthContextType {
  session: Session;
  loading: boolean;
  setOwnerSession: (data: { token: string; restaurant_code: string; restaurant_name: string }) => void;
  setDriverSession: (data: { token: string; driver_id: number; display_name: string; restaurant_code: string }) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const OWNER_KEY = 'vendox_owner_session';
const DRIVER_KEY = 'vendox_driver_session';
const RESTAURANT_CODE_KEY = 'vendox_saved_restaurant_code';

export function getSavedRestaurantCode(): string | null {
  try { return localStorage.getItem(RESTAURANT_CODE_KEY); } catch { return null; }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ownerData = localStorage.getItem(OWNER_KEY);
    const driverData = localStorage.getItem(DRIVER_KEY);

    if (ownerData) {
      try {
        const parsed = JSON.parse(ownerData);
        if (parsed.token) {
          setSession({ role: 'owner', ...parsed });
        }
      } catch {}
    } else if (driverData) {
      try {
        const parsed = JSON.parse(driverData);
        if (parsed.token) {
          setSession({ role: 'driver', ...parsed });
          refreshPushSubscription(parsed.token);
        }
      } catch {}
    }

    setLoading(false);
  }, []);

  const setOwnerSession = useCallback((data: { token: string; restaurant_code: string; restaurant_name: string }) => {
    localStorage.setItem(OWNER_KEY, JSON.stringify(data));
    localStorage.removeItem(DRIVER_KEY);
    setSession({ role: 'owner', ...data });
  }, []);

  const setDriverSession = useCallback((data: { token: string; driver_id: number; display_name: string; restaurant_code: string }) => {
    localStorage.setItem(DRIVER_KEY, JSON.stringify(data));
    localStorage.removeItem(OWNER_KEY);
    localStorage.setItem(RESTAURANT_CODE_KEY, data.restaurant_code);
    setSession({ role: 'driver', ...data });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(OWNER_KEY);
    localStorage.removeItem(DRIVER_KEY);
    setSession(null);
  }, []);

  return (
    <AuthContext.Provider value={{ session, loading, setOwnerSession, setDriverSession, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  if (!base64String) return new Uint8Array(0);
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function refreshPushSubscription(token: string) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.warn('[Vendox Push] المتصفح لا يدعم الإشعارات');
    return;
  }

  try {
    const reg = await navigator.serviceWorker.ready;

    const permission = Notification.permission === 'granted'
      ? 'granted'
      : await Notification.requestPermission();

    if (permission !== 'granted') {
      console.warn('[Vendox Push] المستخدم رفض إذن الإشعارات');
      return;
    }

    const vapidKey = (window as any).__VAPID_PUBLIC_KEY || '';
    if (!vapidKey) {
      console.warn('[Vendox Push] مفتاح VAPID غير متوفر — الإشعارات لن تعمل');
      return;
    }

    let subscription = await reg.pushManager.getSubscription();
    if (!subscription) {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      console.log('[Vendox Push] تم إنشاء اشتراك جديد');
    } else {
      console.log('[Vendox Push] يُعاد إرسال الاشتراك الحالي');
    }

    await api.push.subscribe(subscription.toJSON(), token);
    console.log('[Vendox Push] تم تحديث الاشتراك بالسيرفر بنجاح');
  } catch (err) {
    console.error('[Vendox Push] فشل إعداد الإشعارات:', err);
  }
}
